import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserCheck, UserX, Shield, AlertCircle } from 'lucide-react';
import AssessmentLayout from '../../components/assessment/AssessmentLayout';
import { supabase } from '../../lib/supabase';

export default function AssessmentStart() {
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChoice = async (knowsPerson: boolean) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const insertData: Record<string, any> = {
        user_id: user.id,
        knows_client: knowsPerson,
        status: 'in_progress',
      };
      if (profile?.departmentId) {
        insertData.department_id = profile.departmentId;
      }

      // Try insert without .select().single() first, then fetch
      const { data, error: insertError } = await supabase
        .from('assessments')
        .insert(insertData)
        .select('id');

      let assessmentId: string;

      if (insertError) {
        console.error('Assessment insert failed, using local ID:', insertError);
        assessmentId = crypto.randomUUID();
      } else {
        assessmentId = data?.[0]?.id || crypto.randomUUID();
      }

      // Clear old assessment data before starting new
      sessionStorage.removeItem('selectedCategoryIds');
      sessionStorage.removeItem('assessmentData');
      sessionStorage.removeItem('moduleRecommendation');

      sessionStorage.setItem('currentAssessmentId', assessmentId);
      sessionStorage.setItem('assessmentKnowsPerson', knowsPerson.toString());

      navigate('/assessment/guidance');
    } catch (err) {
      console.error('Error creating assessment:', err);
      setError('Kunne ikke opprette vurdering. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Main Card */}
        <div className="bg-white dark:bg-gray-800 shadow-sm overflow-hidden border-b dark:border-gray-700">
          {/* Header */}
          <div className="bg-blue-600 dark:bg-blue-700 p-8 md:p-12 text-center w-full">
            <div className="inline-flex p-4 bg-white dark:bg-gray-800 rounded-lg mb-4">
              <Shield className="h-12 w-12 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Modulbasert tilnærming for betjenter
            </h1>
            <p className="text-white text-lg">
              Start vurderingsprosessen for innsatte
            </p>
          </div>

          {/* Content */}
          <div className="p-8 md:p-12 w-full">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-white mb-3">
                  Kjenner du innsatte fra før?
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Velg det alternativet som passer best for din situasjon
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              {/* Choice Cards */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* I know the person */}
                <button
                  onClick={() => handleChoice(true)}
                  disabled={loading}
                  className="rounded-lg p-8 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-green-600 dark:hover:border-green-500 shadow-sm hover:shadow-md transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="p-4 bg-green-50 dark:bg-green-900/30 rounded-lg inline-block mb-4">
                    <UserCheck className="h-10 w-10 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    JEG KJENNER INNSATTE
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    Jeg har tidligere erfaring med denne personen
                  </p>
                </button>

                {/* I don't know the person */}
                <button
                  onClick={() => handleChoice(false)}
                  disabled={loading}
                  className="rounded-lg p-8 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-600 dark:hover:border-blue-500 shadow-sm hover:shadow-md transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg inline-block mb-4">
                    <UserX className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    JEG KJENNER IKKE INNSATTE
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    Dette er første gang jeg møter denne personen
                  </p>
                </button>
              </div>

              {/* Info Section */}
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-6 border border-blue-100 dark:border-blue-800">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Viktig informasjon</h4>
                    <p className="text-gray-700 dark:text-gray-300 text-sm">
                      Ditt valg her vil tilpasse veiledningen for å gi deg mest mulig relevant informasjon
                      basert på din kjennskap til den innsatte.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Footer */}
      <div className="bg-white dark:bg-gray-800 px-8 py-4 border-t border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Steg 1 av 5
          </p>
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span>Logg inn: {profile?.fullName || user?.email || 'Anonym'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
